import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import {
  ArrowLeft, Zap, TrendingUp, TrendingDown, AlertTriangle, User, Loader2, Activity,
  ArrowUpRight, ArrowDownRight, Minus,
} from 'lucide-react';
import { Badge } from '../components/ui/badge';
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from 'recharts';

const TEST_LABELS = {
  SJ: 'Squat Jump', CMJ: 'CMJ', CMJ_RIGHT_LEFT: 'CMJ R/L',
  LOADED_CMJ: 'Loaded CMJ', DROP_JUMP: 'Drop Jump',
  CMJ_FREE_ARMS: 'CMJ Free Arms', DRIFT_2D: 'Drift 2D', STIFFNESS: 'Stiffness',
};

export default function AthleteJumpProfilePage() {
  const { athleteId } = useParams();
  const { api } = useAuth();
  const { language } = useLanguage();
  const navigate = useNavigate();
  const t = (en, it) => language === 'IT' ? it : en;

  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [comparisons, setComparisons] = useState({});

  useEffect(() => {
    api.get(`/jump/athlete/${athleteId}/profile`).then(r => setProfile(r.data))
      .catch(() => navigate('/staff/jump'))
      .finally(() => setLoading(false));
  }, [api, athleteId, navigate]);

  // Fetch comparisons for each latest best result
  useEffect(() => {
    if (!profile?.latest) return;
    const fetchComparisons = async () => {
      const comps = {};
      for (const [tt, data] of Object.entries(profile.latest)) {
        if (!data?.id) continue;
        try {
          const r = await api.get(`/jump/results/${data.id}/comparison`);
          comps[tt] = r.data;
        } catch {
          comps[tt] = { has_previous: false };
        }
      }
      setComparisons(comps);
    };
    fetchComparisons();
  }, [api, profile]);

  if (loading) return <div className="flex justify-center py-24"><Loader2 className="w-6 h-6 animate-spin text-zinc-500" /></div>;
  if (!profile) return null;

  const { athlete, latest, best_ever, history, total_tests, alerts } = profile;

  // Build chart data for each test type (iter_120 — protocol-specific series)
  const chartData = {};
  for (const [tt, items] of Object.entries(history)) {
    const sorted = items.filter(r => r.date).sort((a, b) => a.date.localeCompare(b.date));
    if (tt === 'DRIFT_2D') {
      // Merge LEFT/RIGHT per date
      const byDate = new Map();
      for (const r of sorted) {
        const cur = byDate.get(r.date) || { date: r.date };
        const side = (r.side || '').toLowerCase();
        if (side) {
          cur[`${side}_mean_height`] = r.mean_height_cm;
          cur[`${side}_mean_contact`] = r.mean_contact_time_s;
          cur[`${side}_area`] = r.area_cm2;
          cur[`${side}_power_density`] = r.power_density_w_kg_dm2;
        }
        byDate.set(r.date, cur);
      }
      chartData[tt] = Array.from(byDate.values());
    } else if (tt === 'STIFFNESS') {
      chartData[tt] = sorted.map(r => ({
        date: r.date,
        mean_contact: r.mean_contact_time_s,
        mean_flight: r.mean_flight_time_s,
        flight_contact_ratio: r.flight_contact_ratio,
      }));
    } else {
      chartData[tt] = sorted.map(r => ({ date: r.date, height: r.jump_height_cm, rsi: r.rsi }));
    }
  }

  return (
    <div className="space-y-6 fade-in" data-testid="athlete-jump-profile">
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="icon" onClick={() => navigate(-1)} className="text-zinc-400 hover:text-zinc-100">
          <ArrowLeft className="w-5 h-5" />
        </Button>
        <div>
          <h1 className="text-xl font-bold text-zinc-100 flex items-center gap-2">
            <User className="w-5 h-5 text-cyan-400" /> {athlete.name}
          </h1>
          <p className="text-sm text-zinc-500">{athlete.position} • {t('Jump Profile', 'Profilo Salto')}</p>
        </div>
      </div>

      {/* Quick Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Card className="bg-zinc-900/50 border-zinc-800">
          <CardContent className="p-4 text-center">
            <Activity className="w-5 h-5 text-cyan-400 mx-auto mb-1" />
            <div className="text-2xl font-bold text-zinc-100" data-testid="total-tests">{total_tests}</div>
            <div className="text-xs text-zinc-500">{t('Total Tests', 'Test Totali')}</div>
          </CardContent>
        </Card>
        <Card className="bg-zinc-900/50 border-zinc-800">
          <CardContent className="p-4 text-center">
            <Zap className="w-5 h-5 text-emerald-400 mx-auto mb-1" />
            <div className="text-2xl font-bold text-zinc-100" data-testid="test-types">{Object.keys(history).length}</div>
            <div className="text-xs text-zinc-500">{t('Test Types', 'Tipi di Test')}</div>
          </CardContent>
        </Card>
        <Card className="bg-zinc-900/50 border-zinc-800">
          <CardContent className="p-4 text-center">
            <TrendingUp className="w-5 h-5 text-amber-400 mx-auto mb-1" />
            <div className="text-2xl font-bold text-zinc-100" data-testid="best-cmj">
              {best_ever.CMJ?.jump_height_cm || best_ever.SJ?.jump_height_cm || '—'}
            </div>
            <div className="text-xs text-zinc-500">{t('Best CMJ/SJ', 'Miglior CMJ/SJ')} (cm)</div>
          </CardContent>
        </Card>
        <Card className="bg-zinc-900/50 border-zinc-800">
          <CardContent className="p-4 text-center">
            <AlertTriangle className="w-5 h-5 text-red-400 mx-auto mb-1" />
            <div className="text-2xl font-bold text-zinc-100" data-testid="alert-count">{alerts.length}</div>
            <div className="text-xs text-zinc-500">{t('Alerts', 'Allerte')}</div>
          </CardContent>
        </Card>
      </div>

      {/* Alerts */}
      {alerts.length > 0 && (
        <Card className="bg-red-500/5 border-red-500/30">
          <CardHeader className="pb-2"><CardTitle className="text-red-400 text-sm flex items-center gap-2">
            <AlertTriangle className="w-4 h-4" /> {t('Active Alerts', 'Allerte Attive')}</CardTitle></CardHeader>
          <CardContent className="space-y-1">
            {alerts.map((a, i) => (
              <div key={i} className="text-sm flex justify-between p-2 bg-red-500/5 rounded-sm">
                <span className="text-zinc-300">{a.type.replace('_', ' ')}</span>
                <span className="text-red-400 text-xs">
                  {a.drop_pct ? `${a.drop_pct}%` : ''}{a.asymmetry_pct ? `${a.asymmetry_pct}%` : ''}{a.rsi ? `RSI ${a.rsi}` : ''}
                  {a.session_date && ` • ${a.session_date}`}
                </span>
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      {/* Jump Test Comparison */}
      {Object.keys(comparisons).length > 0 && (
        <Card className="bg-zinc-900/50 border-zinc-800" data-testid="jump-comparison-section">
          <CardHeader className="pb-2">
            <CardTitle className="text-zinc-100 text-base flex items-center gap-2">
              <TrendingUp className="w-4 h-4 text-purple-400" />
              {t('Test Comparison', 'Confronto Test')}
            </CardTitle>
            <CardDescription className="text-zinc-500 text-xs">
              {t('Latest result vs. previous best for each test type', 'Ultimo risultato vs. migliore precedente per tipo')}
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {Object.entries(comparisons).map(([tt, comp]) => (
              <div key={tt} className="border border-zinc-800 rounded-sm overflow-hidden" data-testid={`comparison-${tt}`}>
                <div className="px-3 py-2 bg-zinc-800/50 flex items-center justify-between">
                  <span className="text-sm font-semibold text-cyan-400">{TEST_LABELS[tt] || tt}</span>
                  {comp.has_previous && comp.previous?.date && (
                    <span className="text-[10px] text-zinc-500">vs {comp.previous.date}</span>
                  )}
                </div>
                {!comp.has_previous ? (
                  <div className="px-3 py-3 text-sm text-zinc-500" data-testid={`no-prev-${tt}`}>
                    {t('No previous jump test available for comparison.', 'Nessun test precedente disponibile.')}
                  </div>
                ) : (
                  <div className="divide-y divide-zinc-800/50">
                    {/* Metrics rows */}
                    <div className="grid grid-cols-4 gap-2 px-3 py-1.5 text-[10px] text-zinc-500 uppercase tracking-wider">
                      <div>{t('Metric', 'Metrica')}</div>
                      <div className="text-center">{t('Current', 'Attuale')}</div>
                      <div className="text-center">{t('Previous', 'Precedente')}</div>
                      <div className="text-right">Delta</div>
                    </div>
                    {[
                      { key: 'height', label: t('Height', 'Altezza'), unit: 'cm', curKey: 'height_cm', dec: 1 },
                      { key: 'rsi', label: 'RSI', unit: '', curKey: 'rsi', dec: 3 },
                      { key: 'contact_time', label: t('Contact Time', 'Tempo Contatto'), unit: 'ms', curKey: 'contact_time_ms', dec: 0 },
                    ].map(m => {
                      const cur = comp.current?.[m.curKey];
                      const prv = comp.previous?.[m.curKey];
                      const delta = comp.deltas?.[m.key];
                      if (cur == null && prv == null) return null;
                      const dirColor = delta?.direction === 'improved' ? 'text-emerald-400' :
                                       delta?.direction === 'declined' ? 'text-rose-400' : 'text-zinc-400';
                      return (
                        <div key={m.key} className="grid grid-cols-4 gap-2 px-3 py-2 text-sm" data-testid={`comp-row-${m.key}`}>
                          <div className="text-zinc-400 font-medium text-xs">{m.label}</div>
                          <div className="text-center font-semibold text-zinc-100 text-xs">
                            {cur != null ? `${Number(cur).toFixed(m.dec)}${m.unit ? ` ${m.unit}` : ''}` : '—'}
                          </div>
                          <div className="text-center text-zinc-500 text-xs">
                            {prv != null ? `${Number(prv).toFixed(m.dec)}${m.unit ? ` ${m.unit}` : ''}` : '—'}
                          </div>
                          <div className={`text-right text-xs font-medium flex items-center justify-end gap-1 ${dirColor}`}>
                            {delta ? (
                              <>
                                {delta.direction === 'improved' && <ArrowUpRight className="w-3 h-3" />}
                                {delta.direction === 'declined' && <ArrowDownRight className="w-3 h-3" />}
                                {delta.direction === 'stable' && <Minus className="w-3 h-3" />}
                                <span>{delta.abs > 0 ? '+' : ''}{delta.abs.toFixed(m.dec)}</span>
                                {delta.pct !== 0 && <span className="opacity-60">({delta.pct > 0 ? '+' : ''}{delta.pct}%)</span>}
                              </>
                            ) : '—'}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      {/* Latest Results by Test Type */}
      <Card className="bg-zinc-900/50 border-zinc-800">
        <CardHeader className="pb-2">
          <CardTitle className="text-zinc-100 text-base">{t('Latest Results', 'Ultimi Risultati')}</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
            {Object.entries(latest).filter(([_, d]) => d).map(([tt, d]) => (
              <div key={tt} className="p-3 rounded-sm border border-zinc-800 bg-zinc-950/50">
                <div className="flex justify-between items-center mb-2">
                  <span className="text-sm font-semibold text-cyan-400">{TEST_LABELS[tt] || tt}</span>
                  <span className="text-xs text-zinc-500">{d.date}</span>
                </div>
                <div className="text-lg font-bold text-zinc-100">{d.jump_height_cm} cm</div>
                {d.rsi != null && <div className="text-sm text-zinc-400">RSI: {d.rsi}</div>}
                {d.side && <div className="text-xs text-zinc-500">{d.side}</div>}
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Trend Charts (iter_120 — protocol-specific series) */}
      {Object.entries(chartData).filter(([_, d]) => d.length > 1).map(([tt, data]) => (
        <Card key={tt} className="bg-zinc-900/50 border-zinc-800">
          <CardHeader className="pb-2">
            <CardTitle className="text-zinc-100 text-base">{TEST_LABELS[tt] || tt} — {t('Trend', 'Andamento')}</CardTitle>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={220}>
              <LineChart data={data}>
                <CartesianGrid strokeDasharray="3 3" stroke="#333" />
                <XAxis dataKey="date" tick={{ fill: '#888', fontSize: 11 }} />
                <YAxis tick={{ fill: '#888', fontSize: 11 }} domain={['auto', 'auto']} />
                <Tooltip contentStyle={{ backgroundColor: '#1c1c1c', border: '1px solid #333', borderRadius: 4 }}
                  labelStyle={{ color: '#aaa' }} />
                {tt === 'DRIFT_2D' ? (
                  <>
                    <Line type="monotone" dataKey="left_mean_height" stroke="#22d3ee" strokeWidth={2} dot={{ fill: '#22d3ee', r: 3 }} name="L Mean H (cm)" connectNulls />
                    <Line type="monotone" dataKey="right_mean_height" stroke="#f59e0b" strokeWidth={2} dot={{ fill: '#f59e0b', r: 3 }} name="R Mean H (cm)" connectNulls />
                    <Line type="monotone" dataKey="left_mean_contact" stroke="#a3e635" strokeWidth={1.5} strokeDasharray="4 3" dot={false} name="L Mean CT (s)" connectNulls />
                    <Line type="monotone" dataKey="right_mean_contact" stroke="#f472b6" strokeWidth={1.5} strokeDasharray="4 3" dot={false} name="R Mean CT (s)" connectNulls />
                  </>
                ) : tt === 'STIFFNESS' ? (
                  <>
                    <Line type="monotone" dataKey="mean_contact" stroke="#22d3ee" strokeWidth={2} dot={{ fill: '#22d3ee', r: 3 }} name="Mean CT (s)" connectNulls />
                    <Line type="monotone" dataKey="mean_flight" stroke="#a3e635" strokeWidth={2} dot={{ fill: '#a3e635', r: 3 }} name="Mean FT (s)" connectNulls />
                    <Line type="monotone" dataKey="flight_contact_ratio" stroke="#f59e0b" strokeWidth={2} dot={{ fill: '#f59e0b', r: 4 }} name="F/C Ratio" connectNulls />
                  </>
                ) : (
                  <>
                    <Line type="monotone" dataKey="height" stroke="#22d3ee" strokeWidth={2} dot={{ fill: '#22d3ee', r: 4 }} name="Height (cm)" />
                    {data.some(d => d.rsi != null) && (
                      <Line type="monotone" dataKey="rsi" stroke="#f59e0b" strokeWidth={2} dot={{ fill: '#f59e0b', r: 4 }} name="RSI" />
                    )}
                  </>
                )}
              </LineChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      ))}

      {/* Full History */}
      {Object.entries(history).map(([tt, items]) => (
        <Card key={tt} className="bg-zinc-900/50 border-zinc-800">
          <CardHeader className="pb-2">
            <CardTitle className="text-zinc-100 text-base">{TEST_LABELS[tt] || tt} — {t('History', 'Storico')}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-1.5">
            {items.map(r => (
              <div key={r.id} className={`flex items-center justify-between p-2 rounded-sm border ${
                r.best_trial ? 'border-cyan-500/30 bg-cyan-500/5' : 'border-zinc-800 bg-zinc-950/30'
              }`}>
                <div className="flex items-center gap-2">
                  <span className="text-sm text-zinc-200">{r.date || '—'}</span>
                  {r.side && <span className="text-xs text-zinc-500">{r.side}</span>}
                  {r.load_kg != null && <span className="text-xs text-zinc-500">{r.load_kg}kg</span>}
                </div>
                <div className="flex items-center gap-3 text-sm">
                  {tt === 'DRIFT_2D' ? (
                    <span className="text-zinc-100 font-mono text-xs">
                      H {r.mean_height_cm ?? '—'} · CT {r.mean_contact_time_s ?? '—'} · A {r.area_cm2 ?? '—'} · PD {r.power_density_w_kg_dm2 ?? '—'}
                    </span>
                  ) : tt === 'STIFFNESS' ? (
                    <span className="text-zinc-100 font-mono text-xs">
                      CT {r.mean_contact_time_s ?? '—'} · FT {r.mean_flight_time_s ?? '—'} · F/C {r.flight_contact_ratio ?? '—'}
                    </span>
                  ) : (
                    <>
                      <span className="text-zinc-100 font-medium">{r.jump_height_cm} cm</span>
                      {r.rsi != null && <span className="text-cyan-400">RSI {r.rsi}</span>}
                    </>
                  )}
                  {r.best_trial && <span className="text-cyan-400 text-xs">★</span>}
                </div>
              </div>
            ))}
          </CardContent>
        </Card>
      ))}
    </div>
  );
}
